import type IUserBase from './IUserBase';
import type IAuthenticatedUser from './AuthenticatedUser/IAuthenticatedUser';
import type IGuestUser from './GuestUser/IGuestUser';
import UserType from './UserType';

/**
 * Type guard to check if a user is authenticated.
 *
 * Uses `IUserBase` constraint to work with both `IUser` and `DTO<IUser>`.
 *
 * @param user - The user to check.
 * @returns `true` if the user is authenticated, narrowing the type to IAuthenticatedUser.
 *
 * @example
 * ```typescript
 * const user = await getCurrentUser();
 * if (isAuthenticated(user)) {
 *   // user is now typed as IAuthenticatedUser
 *   console.log(user.email, user.account);
 * }
 * ```
 */
export function isAuthenticatedUser<T extends IUserBase>(
  user: T
): user is T & IAuthenticatedUser {
  return user.type === UserType.Authenticated;
}

/**
 * Type guard to check if a user is a guest.
 *
 * Uses `IUserBase` constraint to work with both `IUser` and `DTO<IUser>`.
 *
 * @param user - The user to check.
 * @returns `true` if the user is a guest, narrowing the type to IGuestUser.
 */
export function isGuestUser<T extends IUserBase>(
  user: T
): user is T & IGuestUser {
  return user.type === UserType.Guest;
}
